import { useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Mail, X, Send, Sparkles, Eye, Pencil, Loader2 } from "lucide-react";

export function QuoteFollowUpEmail({ quoteId, onSent }) {
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [instruction, setInstruction] = useState("");
  const [previewHtml, setPreviewHtml] = useState("");
  const [mode, setMode] = useState("edit");
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);

  const openModal = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/admin/quote/${encodeURIComponent(quoteId)}/follow-up-email`);
      setTo(r.data.to);
      setSubject(r.data.subject);
      setBody(r.data.body);
      setMode("edit");
      setPreviewHtml("");
      setOpen(true);
      if (!r.data.to) toast.warning("No email on file for this client — add one via Edit contact first.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load the draft"); }
    setBusy(false);
  };

  const aiDraft = async () => {
    setAiBusy(true);
    try {
      const r = await api.post(`/admin/quote/${encodeURIComponent(quoteId)}/follow-up-email/ai`, { instruction, subject, body });
      if (r.data.subject) setSubject(r.data.subject);
      if (r.data.body) setBody(r.data.body);
      toast.success(r.data.summary || "AI draft ready — review before sending");
      setMode("edit");
    } catch (e) { toast.error(e?.response?.data?.detail || "AI draft failed"); }
    setAiBusy(false);
  };

  const showPreview = async () => {
    if (!body.trim()) { toast.error("Write the email body first"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/admin/quote/${encodeURIComponent(quoteId)}/follow-up-email/preview`, { subject, body });
      setPreviewHtml(r.data.html);
      setMode("preview");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not build the preview"); }
    setBusy(false);
  };

  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/quote/${encodeURIComponent(quoteId)}/follow-up-email/send`, { subject, body });
      toast.success(`Follow-up emailed to ${r.data.to}`);
      setOpen(false);
      onSent?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" && !d.trim().startsWith("<")
        ? d : "Could not send via Gmail — the mailbox connection may need reconnecting in Settings → Connected email.");
    }
    setBusy(false);
  };

  return (
    <>
      <button data-testid="quote-followup-btn" disabled={busy} onClick={openModal}
        className="inline-flex items-center gap-1.5 text-xs font-bold border border-[#E5E7EB] px-2.5 py-1.5 hover:border-[#C79A3B] disabled:opacity-50">
        <Mail size={12} /> Follow-up email
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="qfu-modal">
          <div className="bg-white w-full max-w-2xl border border-[#E5E7EB] shadow-xl max-h-[92vh] flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E7EB] shrink-0">
              <div>
                <div className="label-eyebrow">Follow-up email</div>
                <div className="text-xs text-[#6B7280] mt-0.5">To: <b className="text-[#12263A]">{to || "no email on file"}</b> · Logo, quote summary box &amp; portal sign-in button are added automatically.</div>
              </div>
              <button data-testid="qfu-close" onClick={() => setOpen(false)} className="text-[#6B7280] hover:text-[#12263A]"><X size={18} /></button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              {mode === "edit" ? (
                <div className="space-y-3">
                  <div className="border border-[#E8DFC8] bg-[#FBF7EC] p-2.5 flex gap-2 items-center">
                    <Sparkles size={14} className="text-[#C79A3B] shrink-0" />
                    <input data-testid="qfu-ai-instruction" value={instruction} onChange={e => setInstruction(e.target.value)}
                      placeholder="Optional: tell the AI what angle to take (e.g. mention our summer offer)…"
                      className="flex-1 bg-transparent text-sm focus:outline-none" />
                    <button data-testid="qfu-ai-generate" disabled={aiBusy} onClick={aiDraft}
                      className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-3 py-1.5 bg-[#12263A] text-white hover:bg-[#0C1B2B] disabled:opacity-60">
                      {aiBusy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} {aiBusy ? "Drafting…" : "AI draft"}
                    </button>
                  </div>
                  <div>
                    <label className="text-[11px] text-[#6B7280] uppercase tracking-wider">Subject</label>
                    <input data-testid="qfu-subject" value={subject} onChange={e => setSubject(e.target.value)}
                      className="w-full border border-[#E5E7EB] px-2 py-1.5 text-sm" />
                  </div>
                  <div>
                    <label className="text-[11px] text-[#6B7280] uppercase tracking-wider">Message</label>
                    <textarea data-testid="qfu-body" rows={10} value={body} onChange={e => setBody(e.target.value)}
                      className="w-full border border-[#E5E7EB] px-2 py-1.5 text-sm" />
                  </div>
                </div>
              ) : (
                <iframe data-testid="qfu-preview-frame" title="Email preview" srcDoc={previewHtml}
                  className="w-full h-[420px] border border-[#E5E7EB] bg-white" sandbox="" />
              )}
            </div>
            <div className="flex justify-between gap-2 px-4 py-3 border-t border-[#E5E7EB] shrink-0">
              {mode === "edit" ? (
                <button data-testid="qfu-preview" disabled={busy} onClick={showPreview}
                  className="de-btn-secondary text-xs inline-flex items-center gap-1.5 disabled:opacity-60"><Eye size={13} /> Preview</button>
              ) : (
                <button data-testid="qfu-back-edit" onClick={() => setMode("edit")}
                  className="de-btn-secondary text-xs inline-flex items-center gap-1.5"><Pencil size={13} /> Back to edit</button>
              )}
              <div className="flex gap-2">
                <button data-testid="qfu-cancel" onClick={() => setOpen(false)} className="de-btn-secondary text-xs">Cancel</button>
                <button data-testid="qfu-send" disabled={busy || !to || !subject.trim() || !body.trim()} onClick={send}
                  className="de-btn-primary text-xs inline-flex items-center gap-1.5 disabled:opacity-60">
                  <Send size={13} /> {busy ? "Working…" : "Send email"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
